import React, { useState } from 'react';
import { Building2, Check, ArrowRight, FileText, Users, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import RegisterPopup from '../components/RegisterPopup';
import ServiceProcessSection from '../components/ServiceProcessSection';
import ServiceFAQSection from '../components/ServiceFAQSection';
import SEO from '../components/SEO';

const CompanyFormation = () => {
  const [showPopup, setShowPopup] = useState(false);

  const benefits = [
    'Limited personal liability protection',
    'Tax advantages and flexibility',
    'Enhanced business credibility',
    'Easier access to business funding',
    'Perpetual existence beyond ownership changes',
    'Professional business structure'
  ];

  const services = [
    'LLC Formation',
    'Corporation (C-Corp & S-Corp) Formation',
    'Registered Agent Services',
    'EIN (Employer Identification Number) Application',
    'Operating Agreement Preparation',
    'Corporate Bylaws Drafting',
    'Annual Report Filing Services',
    'Compliance Documentation'
  ];

  const processSteps = [
    {
      icon: 'FileText',
      title: 'Business Structure Consultation',
      description: 'We help you choose the right business entity based on your goals, industry, and future plans.'
    },
    {
      icon: 'Users',
      title: 'Document Preparation',
      description: 'We prepare all necessary formation documents, including Articles of Organization/Incorporation and operating agreements.'
    },
    {
      icon: 'Award',
      title: 'Filing & Compliance',
      description: 'We file your documents with the state and provide guidance on maintaining compliance with all requirements.'
    }
  ];

  const icons = {
    FileText: FileText,
    Users: Users,
    Award: Award
  };

  const formationFAQs = [
    {
      question: 'Should I form an LLC or Corporation?',
      answer: 'The choice depends on your business goals. LLCs offer flexibility and simpler management, ideal for small businesses. Corporations are better for raising capital and going public. We help you evaluate factors like tax implications, liability protection, and future growth plans.'
    },
    {
      question: 'How long does company formation take?',
      answer: 'Standard processing typically takes 1-2 weeks, though timing varies by state. We offer expedited services that can complete formation in 2-3 business days for urgent needs.'
    },
    {
      question: 'What documents do I need to provide?',
      answer: 'Basic information includes your desired company name, business address, member/shareholder information, and management structure. We guide you through the entire documentation process step by step.'
    },
    {
      question: 'Do I need a registered agent?',
      answer: 'Yes, all LLCs and corporations must have a registered agent in the state of formation. We provide registered agent services to receive legal documents and state correspondence on your behalf.'
    },
    {
      question: 'What are the ongoing compliance requirements?',
      answer: 'Requirements vary by state and entity type but typically include annual reports, franchise tax payments, and maintaining accurate records. We provide guidance and optional services to help you stay compliant.'
    }
  ];

  return (
    <div>
      <SEO 
        title="Company Formation Services - LLC & Corporation Setup | IP Protection Center"
        description="Complete business formation services including LLC and Corporation setup. All necessary documentation and filing. Start your business with professional guidance."
        keywords="company formation, LLC formation, corporation setup, business registration, business formation services"
      />
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(177, 91, 55, 0.7)), url('/assets/img/inner-bg.jpg')`,
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Company Formation Services
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Start your business with confidence. We handle all the paperwork and filing requirements for your LLC or Corporation formation.
            </p>
            <Button
              onClick={() => setShowPopup(true)}
              size="lg"
              className="bg-white text-brand-500 hover:bg-brand-50 px-8 py-6 text-lg font-semibold"
            >
              Form Your Company Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why Form a Company Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-aos="zoom-in" data-aos="fade-up">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Form a Company?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Forming a legal business entity like an LLC or Corporation provides crucial protection for your personal assets while offering tax benefits and enhancing your business's professional credibility.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Whether you're a solo entrepreneur, startup founder, or growing business, proper company formation is essential for long-term success. Our expert team makes the process simple and stress-free.
              </p>
            </div>
            <div className="bg-brand-500 rounded-lg p-8 text-white" data-aos="zoom-in" data-aos="fade-up">
              <Building2 className="h-16 w-16 mb-4" />
              <h3 className="text-2xl font-bold mb-4">Build Your Business Foundation</h3>
              <p className="mb-6">
                Proper business formation protects your personal assets and provides the structure needed for growth.
              </p>
              <Button
                onClick={() => setShowPopup(true)}
                className="bg-white text-brand-500 hover:bg-gray-100"
              >
                Get Started Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Benefits of Company Formation
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-aos="fade-up" data-aos="zoom-in">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start bg-white p-6 rounded-lg shadow-md" data-aos="zoom-in" data-aos-delay={index * 100}>
                  <Check className="h-6 w-6  mr-3 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Included Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Our Formation Services Include
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-aos="fade-up" data-aos="zoom-in">
              {services.map((service, index) => (
                <div key={index} className="flex items-start bg-gray-50 p-6 rounded-lg" data-aos="zoom-in" data-aos-delay={index * 100}>
                  <Check className="h-6 w-6  mr-3 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{service}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LLC vs Corporation Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              LLC vs. Corporation: Which is Right for You?
            </h2>
            <div className="grid md:grid-cols-2 gap-8" data-aos="fade-up" data-aos="zoom-in">
              <div className="bg-white rounded-lg p-8 shadow-lg" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Limited Liability Company (LLC)</h3>
                <p className="text-gray-600 mb-6">
                  Best for small businesses, freelancers, and startups seeking flexible management and pass-through taxation.
                </p>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <Check className="h-5 w-5  mr-2 flex-shrink-0 mt-0.5" />
                    <span>Flexible tax options</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5  mr-2 flex-shrink-0 mt-0.5" />
                    <span>Less formal structure</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5  mr-2 flex-shrink-0 mt-0.5" />
                    <span>Easier maintenance</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5  mr-2 flex-shrink-0 mt-0.5" />
                    <span>Personal liability protection</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-8 shadow-lg" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Corporation (C-Corp/S-Corp)</h3>
                <p className="text-gray-600 mb-6">
                  Ideal for businesses planning to raise capital, go public, or have multiple shareholders.
                </p>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <Check className="h-5 w-5  mr-2 flex-shrink-0 mt-0.5" />
                    <span>Easier to raise investment capital</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5  mr-2 flex-shrink-0 mt-0.5" />
                    <span>Can issue stock options</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5  mr-2 flex-shrink-0 mt-0.5" />
                    <span>Perpetual existence</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5  mr-2 flex-shrink-0 mt-0.5" />
                    <span>Enhanced credibility</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <ServiceProcessSection
        title="Our Simple 3-Step Process"
        subtitle="Get your company formed in three easy steps"
        steps={processSteps}
        icons={icons}
      />

      {/* FAQ Section */}
      <ServiceFAQSection
        faqs={formationFAQs}
        imageSrc="/assets/img/home-sec.jpg"
        onContactClick={() => setShowPopup(true)}
      />

      {/* CTA Section */}
      <section className="py-16 bg-brand-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Start Your Business the Right Way
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let our experts handle your company formation so you can focus on building your business.
          </p>
          <Button
            onClick={() => setShowPopup(true)}
            size="lg"
            className="bg-white text-brand-500 hover:bg-gray-100 px-8 py-6 text-lg"
          >
            Form Your Company Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {showPopup && <RegisterPopup onClose={() => setShowPopup(false)} />}
    </div>
  );
};

export default CompanyFormation;